/**
 * Amplitude Analytics Manager
 *
 * Product-analytics destination alongside Statsig. Subscribes to StatsManager and forwards the full
 * funnel event stream to Amplitude. Like Statsig (the primary product-analytics tool), it initializes
 * immediately rather than behind the consent gate.
 *
 * Identity model (email-hash-first, shared with the mobile apps + GA4):
 *   - Anonymous until the email is captured (Amplitude manages its own device id cookie for guests).
 *   - On email capture, set `user_id = SHA-256(normalized email)` — the SAME canonical id GA4 uses
 *     (see core/user-id.ts) and the same recipe the Flutter apps use for logged-in users — so the
 *     web funnel user and the app user resolve to ONE Amplitude user. Amplitude retroactively merges the
 *     prior anonymous (device-id) session into this user on setUserId.
 *   - `email` + `email_sha256` are also set as user properties (the cross-profile join key).
 *
 * Must land in the SAME Amplitude project as the mobile apps (shared API keys in config.ts) — otherwise
 * cross-system unification is impossible.
 */

import * as amplitude from '@amplitude/analytics-browser';
import type { UserRepository } from '../data/user-repository';
import type { Config } from '../types/config.types';
import type { StatsManager } from '../core/stats-manager';
import type { ConsentManager } from '../types/analytics.types';
import type { EventName, EventData, EventMetadata } from '../types/events.types';
import { hashEmailToUserId, normalizeEmail } from '../core/user-id';
import { getRolloutInfo } from '../core/rollout.ts';
import { BUILD_SHA, BUILD_BRANCH } from '../core/build-info.ts';

interface AmplitudeDeps {
  userRepository: UserRepository;
  config: Config;
  statsManager: StatsManager;
  consentManager: ConsentManager;
}

export class AmplitudeManager {
  private initialized = false;
  private userIdentified = false;
  private deps: Partial<AmplitudeDeps> = {};

  /**
   * Initialize Amplitude — accepts dependencies (mirrors StatsigManager).
   */
  init(deps: Partial<AmplitudeDeps>): void {
    if (!deps.config || !deps.statsManager || !deps.consentManager) {
      throw new Error(
        'AmplitudeManager.init() requires config, statsManager, and consentManager dependencies'
      );
    }

    this.deps = deps;

    const amplitudeConfig = this.deps.config!.analytics.amplitude;
    if (!this.deps.config!.analytics.enabled || !amplitudeConfig?.enabled || !amplitudeConfig.apiKey) {
      console.log('Amplitude analytics disabled or not configured');
      return;
    }

    // Amplitude is essential product analytics: gate on essential consent (loads on "Accept All" OR
    // "Essential Only", or where consent isn't required). The debug flag forces eager init.
    if (this.deps.config!.analytics.hardcoreStartupDebug) {
      this.loadAndSubscribe();
    } else {
      this.deps.consentManager!.onEssentialConsentGranted(() => this.loadAndSubscribe());
    }
  }

  /**
   * Initialize the Amplitude SDK and subscribe to StatsManager. Runs once, when essential consent is
   * granted — or immediately under hardcoreStartupDebug.
   */
  private loadAndSubscribe(): void {
    if (this.initialized) {
      return;
    }

    const amplitudeConfig = this.deps.config!.analytics.amplitude!;
    try {
      // No userId at init → stay anonymous until email is captured (Amplitude keeps a device-id cookie).
      // Autocapture: keep sessions + marketing attribution; disable page-view/form/element capture since
      // this is an SPA that emits its own screen_viewed events (avoids double counting & noise).
      amplitude.init(amplitudeConfig.apiKey, {
        autocapture: {
          attribution: true,
          sessions: true,
          pageViews: false,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
        },
      });

      this.initialized = true;
      console.log('Amplitude initialized successfully');

      // Seed user properties available pre-identification (campaign + A/B arm), mirroring Statsig's
      // `custom` block so cohorts line up across the two tools.
      this.setInitialUserProperties();

      // Subscribe to StatsManager events (replays buffered events to this new subscriber).
      this.deps.statsManager!.subscribe(this.handleStatsManagerEvent.bind(this));
      console.log('Amplitude subscribed to StatsManager events');
    } catch (error) {
      console.error('Failed to initialize Amplitude:', error);
    }
  }

  /**
   * Campaign params + A/B variant + rollout arm/build identity as user properties (parity with
   * StatsigManager's `custom`).
   */
  private setInitialUserProperties(): void {
    if (!this.deps.userRepository) {
      return;
    }
    try {
      const identify = new amplitude.Identify();
      const campaign = this.deps.userRepository.getCampaignParams();
      for (const [key, val] of Object.entries(campaign)) {
        if (val) identify.set(key, val as string | number);
      }
      const variant = this.deps.userRepository.getABTestVariant();
      if (variant) identify.set('ab_test_variant', variant);
      // Rollout arm (voc_rollout cookie, set by the rollout-router Worker) is absent outside an
      // active rollout; build identity always exists ('local' outside Cloudflare Pages builds).
      const rollout = getRolloutInfo();
      if (rollout) {
        identify.set('rollout_arm', rollout.arm);
        identify.set('rollout_epoch', String(rollout.epoch));
      } else {
        // Amplitude user properties persist server-side across sessions — a finished
        // rollout must not keep tagging a user's future events with a stale arm.
        identify.unset('rollout_arm');
        identify.unset('rollout_epoch');
      }
      identify.set('build_sha', BUILD_SHA);
      identify.set('build_branch', BUILD_BRANCH);
      amplitude.identify(identify);
    } catch (error) {
      console.warn('Amplitude: failed to set initial user properties:', error);
    }
  }

  /**
   * Handle events from StatsManager — forward the full stream; special-case identification.
   */
  private handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    _eventMetadata: EventMetadata,
    value: string | number | null
  ): void {
    if (!this.initialized) {
      return;
    }

    try {
      if ((eventName === 'user_identified' || eventName === 'email_captured') && eventData.email) {
        // Fire-and-forget; the hash is async (Web Crypto). Guard against unhandled rejection.
        this.identifyUser(String(eventData.email)).catch((error) =>
          console.error('Amplitude: identifyUser failed:', error)
        );
      }

      // Forward every event (Amplitude is a product-analytics destination — full event stream).
      this.trackEvent(eventName, eventData, value);
    } catch (error) {
      console.error('Amplitude: Failed to handle event:', eventName, error);
    }
  }

  /**
   * Set the canonical user_id = SHA-256(email) and the email/email_sha256 user properties.
   * Idempotent: only the first identification with a given email runs.
   */
  async identifyUser(email: string): Promise<void> {
    if (!this.initialized || this.userIdentified) {
      return;
    }
    const normalized = normalizeEmail(email);
    if (!normalized) {
      return;
    }

    try {
      const uid = await hashEmailToUserId(normalized);
      amplitude.setUserId(uid);

      const identify = new amplitude.Identify();
      identify.set('email', normalized);
      identify.set('email_sha256', uid);
      amplitude.identify(identify);

      this.userIdentified = true;
      console.log('Amplitude user identified (hashed):', uid);
    } catch (error) {
      console.error('Amplitude: failed to identify user:', error);
    }
  }

  /**
   * Track a custom event. `value` (when present) is attached as an event property.
   */
  trackEvent(
    eventName: string,
    eventData: EventData = {},
    value: string | number | null = null
  ): void {
    if (!this.initialized) {
      return;
    }
    try {
      const properties: Record<string, unknown> =
        value === null ? { ...eventData } : { ...eventData, value };
      amplitude.track(eventName, properties);
    } catch (error) {
      console.error('Amplitude: failed to track event:', eventName, error);
    }
  }
}

// Create singleton instance (matches statsigManager).
export const amplitudeManager = new AmplitudeManager();
